import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { Building2, ChevronDown, Check, Store } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

/**
 * Branch Selector Dropdown
 *
 * Shows in the app header for users who have access to multiple branches.
 * Super Admin sees all branches; regular users see only assigned ones.
 *
 * Single-branch users don't see the dropdown — their branch is auto-selected.
 */
export function BranchSelector() {
    const {
        branches,
        selectedBranch,
        selectedBranchId,
        selectBranch,
        isAuthenticated,
    } = useAuthStore();

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Don't render if not authenticated or no branches loaded
    if (!isAuthenticated || branches.length === 0) {
        return null;
    }

    if (branches.length === 1) {
        return (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-50/90 dark:bg-emerald-950/40 border border-emerald-200/80 dark:border-emerald-800/80 text-emerald-800 dark:text-emerald-300 shadow-xs">
                <Store size={14} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <span className="max-w-[150px] truncate">{branches[0].name}</span>
            </div>
        );
    }

    const handleSelect = async (branchId: string) => {
        setIsOpen(false);
        try {
            await selectBranch(branchId);
        } catch {
            // Error handling is done in the store
        }
    };

    return (
        <div ref={dropdownRef} className="relative">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={clsx(
                    'flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-semibold transition-all',
                    'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-emerald-400 dark:hover:border-emerald-500',
                    'shadow-xs hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                )}
            >
                <Building2 size={15} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <span className="text-slate-800 dark:text-slate-200 max-w-[130px] sm:max-w-[160px] truncate">
                    {selectedBranch?.name || 'Select Branch'}
                </span>
                <ChevronDown
                    size={13}
                    className={clsx(
                        'text-slate-400 dark:text-slate-500 transition-transform duration-200',
                        isOpen && 'rotate-180'
                    )}
                />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl z-50 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="p-2 border-b border-slate-100 dark:border-slate-800">
                            <p className="text-xs font-semibold text-slate-400 dark:text-slate-400 uppercase tracking-wider px-2">
                                Select Branch
                            </p>
                        </div>

                        {/* Branch List */}
                        <div className="max-h-[300px] overflow-y-auto py-1">
                            {branches.map((branch) => (
                                <button
                                    key={branch.id}
                                    type="button"
                                    onClick={() => handleSelect(branch.id)}
                                    className={clsx(
                                        'w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors text-left',
                                        selectedBranchId === branch.id
                                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                                            : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                                    )}
                                >
                                    <Store
                                        size={16}
                                        className={clsx(
                                            'flex-shrink-0',
                                            selectedBranchId === branch.id
                                                ? 'text-emerald-500'
                                                : 'text-slate-400 dark:text-slate-500'
                                        )}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">
                                            {branch.name}
                                        </p>
                                        {branch.code && (
                                            <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
                                                {branch.code}
                                            </p>
                                        )}
                                    </div>
                                    {branch.isMainBranch && (
                                        <span className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                                            Main
                                        </span>
                                    )}
                                    {selectedBranchId === branch.id && (
                                        <Check
                                            size={16}
                                            className="text-emerald-500 flex-shrink-0"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}